import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnInit,
  viewChild,
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { IconComponent } from '@shared/components/icon/icon.component';
import { FormErrorDirective } from '@shared/directives/form-error.directive';

import { InputComponent } from '../input/input.component';

interface MaskProperties {
  allowNegativeNumbers: boolean;
  dropSpecialCharacters: boolean;
  mask: string;
  precision?: number;
  prefix: string;
  separatorLimit?: string;
  suffix: string;
  thousandSeparator: string;
}

type MaskType = 'limitedPercentage' | 'money' | 'percentage';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormErrorDirective, NgClass, ReactiveFormsModule, IconComponent, NgxMaskDirective],
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputMaskComponent),
    },
    {
      multi: true,
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputMaskComponent),
    },
    provideNgxMask(),
  ],
  selector: 'aux-input-mask',
  styles: [
    `
      :host {
        display: block;
      }

      ::-webkit-input-placeholder {
        font-style: italic;
      }

      ::-moz-placeholder {
        font-style: italic;
      }

      label {
        position: relative;
        display: block;
      }

      .content-container {
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;
      }

      .is-invalid {
        @apply border-aux-error ring-1 ring-aux-error;
      }

      /* remove arrows when number input */
      ::ng-deep input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button,
      input[type='number'] {
        -webkit-appearance: none;
        -moz-appearance: textfield;
        margin: 0;
      }

      :-ms-input-placeholder {
        font-style: italic;
      }
    `,
  ],
  templateUrl: './input-mask.component.html',
})
export class InputMaskComponent extends InputComponent implements AfterViewInit, OnInit {
  inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  @Input() autofocus = false;
  @Input() customErrorMessages: {
    [k: string]: ((fc: NgControl, error: unknown) => string) | string;
  } = {};
  mapMaskByType = new Map<MaskType, Partial<MaskProperties>>([
    [
      'limitedPercentage',
      {
        allowNegativeNumbers: this.allowNegativeNumbers,
        dropSpecialCharacters: false,
        mask: 'percent',
        suffix: '%',
      },
    ],
    [
      'money',
      {
        allowNegativeNumbers: this.allowNegativeNumbers,
        mask: 'separator',
        prefix: this.moneyPrefix,
        thousandSeparator: ',',
      },
    ],
    [
      'percentage',
      {
        allowNegativeNumbers: this.allowNegativeNumbers,
        dropSpecialCharacters: false,
        mask: 'separator.2',
        separatorLimit: '99999',
        suffix: '%',
        thousandSeparator: '',
      },
    ],
  ]);
  @Input() mask? = '';
  maskProperties: Partial<MaskProperties> = {};
  @Input() maskType!: MaskType;

  private _allowNegativeNumbers = false;
  private _moneyPrefix = '$';
  private _precision?: number;

  ngOnInit() {
    this.maskProperties = this.mapMaskByType.get(this.maskType) || { mask: this.mask };
  }

  ngAfterViewInit() {
    // This solution is implemented in this way to focus element inside ag-grid cell
    if (this.autofocus) {
      setTimeout(() => {
        this.inputElement()?.nativeElement?.focus();
      });
    }
  }

  @Input() set allowNegativeNumbers(value: boolean) {
    this._allowNegativeNumbers = value;
    this.updateMaskOptions();
  }

  get allowNegativeNumbers() {
    return this._allowNegativeNumbers;
  }

  private formatLimitedPercentageToNumber(value?: null | string): number {
    if (!value || value === '-') {
      return 0;
    }

    const MIN_RANGE_VALUE = 0;
    const MAX_RANGE_VALUE = 100;

    const parsedValue = parseFloat(`${value}`);

    // Ensure the value is between 0 and 100
    if (parsedValue < MIN_RANGE_VALUE) {
      return MIN_RANGE_VALUE;
    }

    if (parsedValue > MAX_RANGE_VALUE) {
      return MAX_RANGE_VALUE;
    }

    return parsedValue;
  }

  private formatMoneyToNumber(value?: null | string): null | number {
    if (!value || value === '-') {
      return null;
    }

    const regex = new RegExp(`[${this.maskProperties.prefix || '$'},]`, 'g');

    return parseFloat(`${value}`.replace(regex, ''));
  }

  private formatPercentToNumber(value?: null | string): number {
    if (!value || value === '-') {
      return 0;
    }

    return parseFloat(`${value}`);
  }

  @Input() set moneyPrefix(prefix: string) {
    this._moneyPrefix = prefix;
    this.updateMaskOptions();
  }

  get moneyPrefix() {
    return this._moneyPrefix;
  }

  onChangeValue(value: null | string) {
    let formattedValue: null | number | string = value;

    if (this.maskType === 'money') {
      formattedValue = this.formatMoneyToNumber(value);
    }

    if (this.maskType === 'percentage') {
      formattedValue = this.formatPercentToNumber(value);
    }

    if (this.maskType === 'limitedPercentage') {
      formattedValue = this.formatLimitedPercentageToNumber(value);
    }

    this.onChange(formattedValue);
  }

  @Input() set precision(value: number) {
    this._precision = value;
    this.updateMaskOptions();
  }

  get precision(): number | undefined {
    return this._precision;
  }

  private updateMaskOptions() {
    this.maskProperties = this.mapMaskByType.get(this.maskType) || { mask: this.mask };
    this.maskProperties.allowNegativeNumbers = this._allowNegativeNumbers;

    if (this.maskType === 'money') {
      this.maskProperties.prefix = this.moneyPrefix;
    }

    if (this.maskType === 'limitedPercentage') {
      this.maskProperties.mask = this.precision ? `percent.${this.precision}` : 'percent';
    }

    if (this.maskType === 'percentage') {
      this.maskProperties.mask = this.precision ? `separator.${this.precision}` : 'separator';
    }
  }
}
